import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import logo from "../asset/img/Excite WLL.png";
import Footer from "../layout/Footer";

const PRIMARY = "#a7cc48";
const DARK = "#2c2d3f";
const DARK2 = "#1e1f2e";

/* ── Scroll-reveal wrapper ── */
function FadeIn({ children, delay = 0, from = "up", style = {} }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const translate =
    from === "up" ? "translateY(28px)" :
    from === "left" ? "translateX(-28px)" :
    from === "right" ? "translateX(28px)" : "none";

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : translate,
        transition: `opacity 0.55s ease ${delay}ms, transform 0.55s ease ${delay}ms`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

const benefits = [
  {
    icon: "bi bi-cash-coin",
    title: "Commission on every signup",
    desc: "When a merchant uses your code to subscribe, you get paid. That's pretty much it.",
  },
  {
    icon: "bi bi-shield-check",
    title: "Nothing to invest",
    desc: "No fees, no training costs, no catch. Just sign up and start sharing your link.",
  },
  {
    icon: "bi bi-bar-chart-line",
    title: "Your own dashboard",
    desc: "See who signed up, who's still active, and how much you've earned. All in one place.",
  },
  {
    icon: "bi bi-share",
    title: "Share however you like",
    desc: "WhatsApp, a link in your bio, a QR code at an event. However you reach people works.",
  },
];

const steps = [
  {
    num: "01",
    label: "Sign up",
    detail: "Takes a few minutes. Free.",
  },
  {
    num: "02",
    label: "Get verified",
    detail: "We check your details and activate your account.",
  },
  {
    num: "03",
    label: "Share and collect",
    detail: "Send out your link. Get paid when merchants sign up.",
  },
];

export default function Index() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div style={s.page}>
      {/* Keyframe injection */}
      <style>{`
        @keyframes heroTextIn {
          from { opacity: 0; transform: translateY(32px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes heroImgIn {
          from { opacity: 0; transform: translateX(40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-10px); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.5; }
        }
        .nav-login:hover { color: #fff !important; }
        .btn-ghost:hover {
          background: rgba(255,255,255,0.14) !important;
          border-color: rgba(255,255,255,0.35) !important;
        }
        .btn-primary-link:hover {
          filter: brightness(1.08);
          transform: translateY(-1px);
        }
        .benefit-card:hover {
          border-color: ${PRIMARY} !important;
          transform: translateY(-4px);
          box-shadow: 0 8px 28px rgba(0,0,0,0.08) !important;
        }
      `}</style>

      {/* NAV */}
      <nav style={{ ...s.nav, ...(scrolled ? s.navScrolled : {}) }}>
        <img src={logo} alt="Excite Enterprise" style={s.navLogo} />
        <div style={s.navLinks}>
          <Link to="/accounts?q=login" className="nav-login" style={s.navLogin}>Log in</Link>
          <Link to="/accounts?q=sign-up" style={s.navCta}>Join as agent</Link>
        </div>
      </nav>

      {/* HERO */}
      <section style={s.hero}>
        <div style={s.heroGlow} />

        {/* Left: text */}
        <div style={s.heroLeft}>
          <div style={{ animation: "heroTextIn 0.6s ease 0.1s both" }}>
            <div style={s.heroBadge}>
              <span style={{ ...s.heroBadgeDot, animation: "pulse 2s ease infinite" }} />
              Now accepting agents
            </div>
          </div>

          <div style={{ animation: "heroTextIn 0.6s ease 0.25s both" }}>
            <h1 style={s.heroH1}>
              Get paid to bring<br />
              merchants <span style={s.heroAccent}>on board.</span>
            </h1>
          </div>

          <div style={{ animation: "heroTextIn 0.6s ease 0.4s both" }}>
            <p style={s.heroSub}>
              We give you a personal referral code. Every merchant who uses it to sign up earns you a commission. That's the whole idea.
            </p>
          </div>

          <div style={{ animation: "heroTextIn 0.6s ease 0.55s both" }}>
            <div style={s.heroCtas}>
              <Link to="/accounts?q=sign-up" className="btn-primary-link" style={{ ...s.btnPrimary, transition: "filter 0.2s, transform 0.2s" }}>
                Register free
              </Link>
              <Link to="/accounts?q=login" className="btn-ghost" style={s.btnGhost}>
                I have an account
              </Link>
            </div>
          </div>

          <div style={{ animation: "heroTextIn 0.6s ease 0.7s both" }}>
            <div style={s.heroStats}>
              <div style={s.statItem}>
                <span style={s.statVal}>Free</span>
                <span style={s.statLbl}>to join</span>
              </div>
              <div style={s.statDivider} />
              <div style={s.statItem}>
                <span style={s.statVal}>No cap</span>
                <span style={s.statLbl}>on earnings</span>
              </div>
              <div style={s.statDivider} />
              <div style={s.statItem}>
                <span style={s.statVal}>Your pace</span>
                <span style={s.statLbl}>no targets</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right: image */}
        <div style={{ animation: "heroImgIn 0.7s ease 0.3s both", flex: "1 1 380px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={s.imgFrame}>
            <div style={s.imgGlow} />
            <img
              src="/images/agents.png"
              alt="Excite sales agents"
              style={{ ...s.heroImg, animation: "float 5s ease-in-out infinite" }}
            />
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section style={s.section}>
        <div style={s.sectionInner}>
          <FadeIn>
            <p style={s.sectionEyebrow}>Why it works</p>
            <h2 style={s.sectionH2}>What you actually get out of it</h2>
          </FadeIn>
          <div style={s.grid4}>
            {benefits.map((b, i) => (
              <FadeIn key={i} delay={i * 80} from="up">
                <div
                  className="benefit-card"
                  style={{
                    ...s.benefitCard,
                    transition: "border-color 0.25s, transform 0.25s, box-shadow 0.25s",
                  }}
                >
                  <div style={s.benefitIcon}>
                    <i className={b.icon} style={{ fontSize: 20, color: PRIMARY }} />
                  </div>
                  <h3 style={s.benefitTitle}>{b.title}</h3>
                  <p style={s.benefitDesc}>{b.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section style={{ ...s.section, background: DARK, padding: "72px 6vw" }}>
        <div style={s.sectionInner}>
          <FadeIn>
            <p style={{ ...s.sectionEyebrow, color: "rgba(167,204,72,0.7)" }}>How it works</p>
            <h2 style={{ ...s.sectionH2, color: "#fff" }}>Three steps and you're set</h2>
          </FadeIn>
          <div style={s.stepsRow}>
            {steps.map((st, i) => (
              <FadeIn key={i} delay={i * 120} from="up" style={{ flex: "1 1 200px", maxWidth: 280 }}>
                <div style={s.stepCard}>
                  <div style={s.stepNum}>{st.num}</div>
                  <h3 style={s.stepLabel}>{st.label}</h3>
                  <p style={s.stepDetail}>{st.detail}</p>
                </div>
                {i < steps.length - 1 && (
                  <div style={s.stepConnector} />
                )}
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section style={s.ctaStrip}>
        <FadeIn from="up">
          <div style={s.ctaInner}>
            <h2 style={s.ctaH2}>Sounds good?</h2>
            <p style={s.ctaSub}>
              It takes a few minutes to sign up. Your referral code is ready as soon as your account is approved.
            </p>
            <Link to="/accounts?q=sign-up" className="btn-primary-link" style={{ ...s.btnPrimary, fontSize: 15, padding: "14px 36px", transition: "filter 0.2s, transform 0.2s" }}>
              Create your agent account
            </Link>
          </div>
        </FadeIn>
      </section>

      <Footer />
    </div>
  );
}

const s = {
  page: {
    fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
    background: "#f5f6f8",
    minHeight: "100vh",
    overflowX: "hidden",
  },

  nav: {
    position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "20px 6vw",
    transition: "background 0.3s, box-shadow 0.3s, padding 0.3s",
  },
  navScrolled: {
    background: "rgba(22,23,34,0.96)",
    backdropFilter: "blur(14px)",
    boxShadow: "0 1px 24px rgba(0,0,0,0.35)",
    padding: "14px 6vw",
  },
  navLogo: { height: 34 },
  navLinks: { display: "flex", alignItems: "center", gap: 10 },
  navLogin: {
    color: "rgba(255,255,255,0.65)", textDecoration: "none",
    fontSize: 14, fontWeight: 500, padding: "8px 14px", borderRadius: 7,
    transition: "color 0.2s",
  },
  navCta: {
    background: PRIMARY, color: DARK, textDecoration: "none",
    fontSize: 14, fontWeight: 700, padding: "9px 18px", borderRadius: 8,
    transition: "filter 0.2s",
  },

  hero: {
    background: `linear-gradient(140deg, ${DARK2} 0%, ${DARK} 55%, #1d2b14 100%)`,
    minHeight: "100vh",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "center",
    padding: "110px 7vw 80px",
    gap: 48,
    position: "relative",
    overflow: "hidden",
  },
  heroGlow: {
    position: "absolute",
    top: "40%", left: "38%",
    width: 560, height: 560,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(167,204,72,0.1) 0%, transparent 68%)",
    pointerEvents: "none",
    transform: "translate(-50%,-50%)",
  },
  heroLeft: {
    flex: "1 1 340px",
    maxWidth: 560,
    position: "relative",
    zIndex: 1,
  },
  heroBadge: {
    display: "inline-flex", alignItems: "center", gap: 8,
    background: "rgba(167,204,72,0.1)",
    border: "1px solid rgba(167,204,72,0.28)",
    borderRadius: 20, padding: "5px 14px",
    fontSize: 12, color: PRIMARY, fontWeight: 600,
    marginBottom: 22, letterSpacing: 0.4,
  },
  heroBadgeDot: {
    width: 6, height: 6, borderRadius: "50%",
    background: PRIMARY, boxShadow: `0 0 7px ${PRIMARY}`,
    display: "inline-block",
  },
  heroH1: {
    fontSize: "clamp(34px, 5.5vw, 54px)",
    fontWeight: 800, color: "#fff",
    lineHeight: 1.18, marginBottom: 18, letterSpacing: "-0.5px",
  },
  heroAccent: { color: PRIMARY },
  heroSub: {
    fontSize: 16, color: "rgba(255,255,255,0.58)",
    lineHeight: 1.75, marginBottom: 32, maxWidth: 460,
  },
  heroCtas: { display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 36 },
  heroStats: {
    display: "flex", alignItems: "center", gap: 0,
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12, padding: "14px 20px",
    width: "fit-content",
  },
  statItem: { display: "flex", flexDirection: "column", alignItems: "center", padding: "0 20px" },
  statVal: { fontSize: 16, fontWeight: 800, color: "#fff" },
  statLbl: { fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 2, textTransform: "uppercase", letterSpacing: 0.4 },
  statDivider: { width: 1, height: 34, background: "rgba(255,255,255,0.1)" },

  imgFrame: {
    position: "relative",
    borderRadius: 20,
    overflow: "visible",
    maxWidth: 460,
    width: "100%",
  },
  imgGlow: {
    position: "absolute",
    inset: -20,
    borderRadius: 32,
    background: "radial-gradient(circle at 50% 60%, rgba(167,204,72,0.18) 0%, transparent 70%)",
    pointerEvents: "none",
    zIndex: 0,
  },
  heroImg: {
    width: "100%",
    maxWidth: 460,
    borderRadius: 18,
    display: "block",
    position: "relative",
    zIndex: 1,
    filter: "drop-shadow(0 24px 48px rgba(0,0,0,0.5))",
  },

  btnPrimary: {
    background: PRIMARY, color: DARK, textDecoration: "none",
    fontWeight: 700, fontSize: 14, padding: "12px 26px",
    borderRadius: 9, display: "inline-block",
    boxShadow: "0 4px 18px rgba(167,204,72,0.3)",
  },
  btnGhost: {
    background: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.75)",
    textDecoration: "none", fontWeight: 600, fontSize: 14, padding: "12px 26px",
    borderRadius: 9, display: "inline-block",
    border: "1px solid rgba(255,255,255,0.14)",
    transition: "background 0.2s, border-color 0.2s",
  },

  section: { padding: "80px 6vw", background: "#fff" },
  sectionInner: { maxWidth: 1080, margin: "0 auto" },
  sectionEyebrow: {
    fontSize: 11, fontWeight: 700, color: PRIMARY,
    textTransform: "uppercase", letterSpacing: 1.8, marginBottom: 10,
  },
  sectionH2: {
    fontSize: "clamp(22px, 3vw, 34px)", fontWeight: 800,
    color: DARK, marginBottom: 44, maxWidth: 480, lineHeight: 1.3,
  },

  grid4: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: 20,
  },
  benefitCard: {
    background: "#f9fafa", borderRadius: 14, padding: "26px 22px",
    border: "1.5px solid #ebebeb",
  },
  benefitIcon: {
    width: 44, height: 44, borderRadius: 10,
    background: "rgba(167,204,72,0.1)",
    display: "flex", alignItems: "center", justifyContent: "center",
    marginBottom: 14,
  },
  benefitTitle: { fontSize: 15, fontWeight: 700, color: DARK, marginBottom: 7 },
  benefitDesc: { fontSize: 13, color: "#6b6b6b", lineHeight: 1.65, margin: 0 },

  stepsRow: {
    display: "flex", flexWrap: "wrap", alignItems: "flex-start",
    gap: 8,
  },
  stepCard: { padding: "8px 8px 8px 0", textAlign: "left" },
  stepNum: {
    fontSize: 38, fontWeight: 900, color: "rgba(167,204,72,0.2)",
    lineHeight: 1, marginBottom: 8, fontVariantNumeric: "tabular-nums",
  },
  stepLabel: { fontSize: 17, fontWeight: 700, color: "#fff", marginBottom: 6 },
  stepDetail: { fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.6, margin: 0 },
  stepConnector: {
    flex: "0 0 40px", height: 1,
    background: "rgba(167,204,72,0.2)",
    marginTop: 28, alignSelf: "flex-start",
  },

  ctaStrip: {
    background: `linear-gradient(140deg, ${DARK} 0%, ${DARK2} 100%)`,
    padding: "80px 6vw", textAlign: "center",
  },
  ctaInner: { maxWidth: 560, margin: "0 auto" },
  ctaH2: {
    fontSize: "clamp(26px, 4vw, 38px)", fontWeight: 800,
    color: "#fff", marginBottom: 14, letterSpacing: "-0.3px",
  },
  ctaSub: {
    fontSize: 15, color: "rgba(255,255,255,0.5)",
    marginBottom: 32, lineHeight: 1.7,
  },

};
